import { BlocksNode } from '../types';
import { Direction } from './types';
import { getRootNode } from './util';

export type BlockGrid = {
  set: (col: number, row: number, value: BlocksNode) => void;
  get: (col: number, row: number) => BlocksNode | null;
  getBlock: (col: number, row: number, direction: Direction) => number;
  isInside: (col: number, row: number) => boolean;
  isBlocked: (col: number, row: number, whitelist: Set<BlocksNode>) => boolean;
};

declare global {
  interface Set<T> {
    isSubsetOf<T>(other: ReadonlySet<unknown>): boolean;
  }
}

export function getBlockGrid(group: BlocksNode): BlockGrid {
  /******************************* */
  const hash = (col: number, row: number) => col * 10000 + row;
  const map = new Map<number, Set<BlocksNode>>();

  const doit = (node: BlocksNode) => {
    if (node.layoutData) {
      const { col, colSpan, row, rowSpan } = node.layoutData;

      for (let i = 0; i < colSpan; ++i) {
        for (let j = 0; j < rowSpan; ++j) {
          const key = hash(col + i, row + j);
          let set = map.get(key);

          if (!set) {
            set = new Set<BlocksNode>();
            map.set(key, set);
          }

          set.add(node);
        }
      }
    }

    if (node.children) {
      for (const child of node.children) {
        doit(child);
      }
    }
  };

  doit(getRootNode(group));

  const isBlocked = (
    col: number,
    row: number,
    whitelist: Set<BlocksNode>
  ): boolean => {
    const key = hash(col, row);
    const set = map.get(key);

    if (!set) {
      return true;
    }

    return !set.isSubsetOf(whitelist);
  };

  const rows = new Map<number, Map<number, BlocksNode | null>>();
  let cacheKey: number | undefined; //  = undefined cached last row index
  let cacheRow: Map<number, BlocksNode | null> | undefined; //  = undefined cached last row

  const refreshCacheRow = (row: number) => {
    if (row !== cacheKey) {
      cacheKey = row;
      cacheRow = rows.get(cacheKey);
    }

    return cacheRow;
  };

  const testBounds = (col: number, row: number) => {
    if (
      !group.colSize ||
      col < 0 ||
      col >= group.colSize.length ||
      !group.rowSize ||
      row < 0 ||
      row >= group.rowSize.length
    ) {
      return false;
    }

    return true;
  };

  const set = (col: number, row: number, value: BlocksNode) => {
    if (!testBounds(col, row)) {
      return;
    }

    refreshCacheRow(row);

    if (!cacheRow) {
      cacheRow = new Map<number, BlocksNode | null>();
      rows.set(row, cacheRow);
    }

    cacheRow.set(col, value);
  };

  const get = (col: number, row: number) => {
    if (!testBounds(col, row)) {
      return null;
    }

    refreshCacheRow(row);

    return cacheRow?.get(col) ?? null;
  };

  const getBlock = (col: number, row: number, direction: Direction) => {
    if (!testBounds(col, row)) {
      switch (direction) {
        case Direction.Left:
        case Direction.Right:
          return -Infinity;

        case Direction.Up:
        case Direction.Down:
          return +Infinity;
      }

      return 0;
    }

    if (direction === Direction.Left) {
      refreshCacheRow(row);

      if (cacheRow) {
        let c = col;

        while (c > 0) {
          if (cacheRow.has(c - 1)) {
            return c;
          }

          --c;
        }
      }

      return -Infinity;
    }

    if (direction === Direction.Up) {
      let r = row;

      while (r > 0) {
        if (get(col, r - 1)) {
          return r;
        }

        --r;
      }

      return -Infinity;
    }

    if (direction === Direction.Right) {
      refreshCacheRow(row);

      if (cacheRow) {
        let c = col;

        while (c < group.colSize!.length - 1) {
          if (cacheRow.has(c + 1)) {
            return c;
          }

          ++c;
        }
      }

      return +Infinity;
    }

    if (direction === Direction.Down) {
      let r = row;

      while (r < group.rowSize!.length - 1) {
        if (get(col, r + 1)) {
          return r;
        }

        ++r;
      }

      return +Infinity;
    }

    return 0;
  };
  /*
  const isColEmpty = (col: number) => {
    const rowSize = group.rowSize?.length;

    if (!rowSize) {
      return false;
    }

    for (let row = 0; row < rowSize; ++row) {
      if (get(col, row)) {
        return false;
      }
    }

    return true;
  };

  const isRowEmpty = (row: number) => {
    const colSize = group.colSize?.length;

    if (!colSize) {
      return false;
    }

    refreshCacheRow(row);

    if (cacheRow) {
      for (let col = 0; col < colSize; ++col) {
        if (cacheRow.has(col)) {
          return false;
        }
      }
    }

    return true;
  };
*/
  if (group.children) {
    for (const child of group.children) {
      for (let i = child.layoutData!.rowSpan - 1; i >= 0; --i) {
        for (let j = child.layoutData!.colSpan - 1; j >= 0; --j) {
          set(child.layoutData!.col + j, child.layoutData!.row + i, child);
        }
      }
    }
  }

  return {
    set: set,
    get: get,
    getBlock: getBlock,
    isInside: testBounds,

    isBlocked: isBlocked,
  };
}
